"use client";

import React, { useEffect, useState } from "react";
import BottomCurves from "../curves/bottomCurves";
import RevealElement from "../revealElement";
import ServiceCard from "./serviceCard";
import { ServiceTab } from "../../../global";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useLenisContext } from "../provider/lenisProvider";

// 🔧 Utility to normalize tab slugs
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/&/g, "and") // Replace '&' with 'and'
    .replace(/\s+/g, "-") // Replace all spaces with '-'
    .replace(/[^\w-]+/g, ""); // Remove all non-word characters
}

type ServicesOverviewProps = {
  data: {
    heading: string;
    description: string;
    serviceTabs: ServiceTab[];
    waveBgColor: { waveBgColor: string };
  };
};

export default function ServicesOverview({ data }: ServicesOverviewProps) {
  const { lenisStop, lenisStart } = useLenisContext();

  const [activeTab, setActiveTab] = useState(
    slugify(data.serviceTabs[0].serviceCategory)
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownText, setDropdownText] = useState(
    data.serviceTabs[0].serviceCategory
  );

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isPopupClosing, setIsPopupClosing] = useState(false);

  const handleTabChange = (category: string) => {
    const formatted = slugify(category);
    setActiveTab(formatted);
    setDropdownText(category);

    const url = new URL(window.location.href);
    url.searchParams.set("tab", formatted);
    window.history.pushState({}, "", url);
  };

  useEffect(() => {
    const savedService = sessionStorage.getItem("service");
    const serviceCategory = savedService
      ? slugify(savedService)
      : slugify(data.serviceTabs[0].serviceCategory);

    setActiveTab(serviceCategory);
    setDropdownText(savedService || data.serviceTabs[0].serviceCategory);
  }, [data.serviceTabs]);

  useEffect(() => {
    const queryTab = slugify(
      new URLSearchParams(window.location.search).get("tab") || ""
    );

    const matchedService = data.serviceTabs.find(
      (tab) => slugify(tab.serviceCategory) === queryTab
    );

    if (matchedService) {
      const formatted = slugify(matchedService.serviceCategory);
      setActiveTab(formatted);
      setDropdownText(matchedService.serviceCategory);
    } else {
      // fallback to first tab
      const defaultCategory = data.serviceTabs[0].serviceCategory;
      setActiveTab(slugify(defaultCategory));
      setDropdownText(defaultCategory);
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = isPopupOpen ? "hidden" : "auto";
    isPopupOpen ? lenisStop() : lenisStart();
  }, [isPopupOpen, lenisStop, lenisStart]);

  const renderTabs = (isMobile = false) =>
    data.serviceTabs.map(({ serviceCategory }, i) => {
      const formatted = slugify(serviceCategory);
      const isActive = activeTab === formatted;

      const classes = isMobile
        ? "flex body-small-fontsize leading-[1] w-full font-medium px-6 py-3 duration-200 h-fit justify-center whitespace-nowrap"
        : `flex justify-center body-small-fontsize leading-[1] font-medium px-6 py-3 duration-200 border-[1px] border-purple rounded-full h-fit w-full md:w-max whitespace-nowrap cursor-pointer text-center ${isActive
          ? "bg-purple text-white"
          : "text-purple bg-transparent hover:bg-purple hover:text-white"
        }`;

      return (
        <span
          key={i}
          className={classes}
          role="button"
          onClick={() => {
            handleTabChange(serviceCategory);
            if (isMobile) setDropdownOpen(false);
          }}
        >
          {serviceCategory}
        </span>
      );
    });

  return (
    <section className="bg-rainbow pt-12 pb-12 md:pb-32 xl:pb-52">
      <RevealElement>
        <h2 className="text-[#263244] text-center">{data.heading}</h2>
        <p className="text-center md:px-20">{data.description}</p>
      </RevealElement>

      <div className="mt-12 mb-8">
        <RevealElement>
          {data.serviceTabs.length > 1 && (
            <>
              {/* Mobile Dropdown */}
              <div className="relative flex md:hidden flex-col gap-y-3 mb-8">
                <span
                  className="relative flex items-center justify-between w-full px-6 py-3 font-medium text-white bg-purple border border-purple rounded-full z-40 body-small-fontsize"
                  onClick={() =>
                    setDropdownOpen((prev) => !prev)
                  }
                >
                  {dropdownText}
                  <ChevronDownIcon
                    className={`w-6 h-6 absolute right-5 transition-transform ${dropdownOpen
                      ? "rotate-180"
                      : "rotate-0"
                      }`}
                  />
                </span>
                <div
                  className={`absolute top-[125%] left-0 right-0 w-full z-30 bg-[#E4CEF8] border border-purple rounded-b-3xl pt-8 -translate-y-7 transition-opacity duration-150 ${dropdownOpen
                    ? "opacity-100 pointer-events-auto"
                    : "opacity-0 pointer-events-none"
                    }`}
                >
                  {renderTabs(true)}
                </div>
              </div>

              {/* Desktop Tabs */}
              <div className="hidden md:flex flex-wrap justify-center gap-4 mb-8 max-w-5xl 5xl:max-w-full mx-auto">
                {renderTabs()}
              </div>
            </>
          )}
          {/* Service Cards */}
          <div className="flex flex-col gap-5">
            {data.serviceTabs.map(
              ({ serviceCategory, service }, i) => {
                const formatted = slugify(serviceCategory);

                return (
                  formatted === activeTab && (
                    <ServiceCard
                      key={i}
                      heading={service.heading}
                      description={service.description}
                      button={service.button}
                      slides={service.slides}
                      redirectLink={service.button.link}
                      setIsPopupOpen={setIsPopupOpen}
                    />
                  )
                );
              }
            )}
          </div>
        </RevealElement>
      </div>

      <BottomCurves
        color={data.waveBgColor.waveBgColor}
        direction="right"
      />

      {/* Popup */}
      {isPopupOpen && (
        <div
          className={`fixed top-0 left-0 w-full h-full flex items-center justify-center z-50 p-4 md:p-20 bg-[#00000080] transition-opacity duration-500 ${isPopupClosing ? "opacity-0" : "opacity-100"
            }`}
        >
          <div className="relative w-full rounded-2xl">
            <button
              className="absolute -right-5 -top-5 flex items-center justify-center bg-green w-10 h-10 rounded-full"
              onClick={() => {
                setIsPopupClosing(true);
                setTimeout(() => {
                  setIsPopupOpen(false);
                  setIsPopupClosing(false);
                }, 500);
              }}
            >
              <XMarkIcon className="w-6 h-6 text-purple" />
            </button>
            <iframe
              className="w-full aspect-[4/3] md:aspect-[1415/595] h-full rounded-3xl"
              src={
                data.serviceTabs[2].service.slides[0]
                  .redirectLink
              }
              title={`KD Showreel ${new Date().getFullYear()}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              frameBorder={0}
            ></iframe>
          </div>
        </div>
      )}
    </section>
  );
}
